import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";

const ORIGIN = "https://tatoys.cn";

export default function ArticlePage() {
  const { id } = useParams();
  const article = articles.find((a) => a.id === id);

  const related = article
    ? articles.filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    : [];

  const headings = article ? article.content.filter((b) => b.type === "h2") : [];

  useSeo({
    title: article ? `${article.title}｜黑料在线` : "话题未找到｜黑料在线",
    description: article
      ? article.excerpt
      : "该话题可能已被合并或调整，返回首页查看最新的吃瓜速览内容。",
    keywords: article ? article.tags.join(",") : "黑料在线",
    path: `/article/${id ?? ""}`,
    image: article?.cover,
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            image: article.cover,
            datePublished: article.date,
            dateModified: article.date,
            articleSection: article.categoryName,
            keywords: article.tags.join(","),
            author: { "@type": "Organization", name: "黑料在线" },
            publisher: {
              "@type": "Organization",
              name: "黑料在线",
              url: `${ORIGIN}/`,
            },
            mainEntityOfPage: `${ORIGIN}/article/${article.id}`,
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${ORIGIN}/` },
              { "@type": "ListItem", position: 2, name: "专题库", item: `${ORIGIN}/topics` },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
                item: `${ORIGIN}/article/${article.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="flex w-full flex-1 items-center justify-center px-4 py-24">
          <div className="max-w-md text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-2xl text-primary-700">
              <i className="ri-search-eye-line" />
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这个话题</h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              它可能已被合并到其他专题，或链接里的编号有误。去热榜看看现在大家都在聊什么。
            </p>
            <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
              <Link
                to="/hot"
                className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-label text-background-50 cursor-pointer"
              >
                打开吃瓜热榜
              </Link>
              <Link
                to="/topics"
                className="whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm font-label text-foreground-800 cursor-pointer"
              >
                浏览专题库
              </Link>
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full flex-1">
        <article>
          <section className="w-full border-b border-background-200 bg-background-100 px-4 py-10 md:px-8 md:py-14">
            <Breadcrumbs
              items={[
                { label: "首页", path: "/" },
                { label: "专题库", path: "/topics" },
                { label: article.title },
              ]}
            />
            <div className="mt-5 flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-full bg-primary-100 px-3 py-1 font-label text-primary-700">
                {article.categoryName}
              </span>
              {article.tags.map((t) => (
                <span key={t} className="rounded-full bg-background-200 px-3 py-1 font-label text-foreground-700">
                  #{t}
                </span>
              ))}
            </div>
            <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-tight tracking-tight text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                <time dateTime={article.date}>{article.date}</time>
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                读完约 {article.readTime}
              </span>
              <span className="flex items-center gap-1.5 text-primary-600">
                <i className="ri-fire-line" />
                热度 {article.hot}
              </span>
            </div>
          </section>

          <div className="w-full px-4 py-10 md:px-8 md:py-14">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_300px]">
              <div>
                <div className="relative w-full overflow-hidden rounded-card" style={{ aspectRatio: "16 / 9" }}>
                  <img
                    src={article.cover}
                    alt={`${article.title} - 话题配图`}
                    title={`${article.title} · 黑料在线`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>

                <div className="mt-8 rounded-card border-l-4 border-primary-500 bg-primary-50 p-5 md:p-6">
                  <h2 className="flex items-center gap-2 font-heading text-base font-bold text-primary-800">
                    <i className="ri-focus-3-line" />
                    结论先看
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-800 md:text-base">
                    {article.excerpt}
                  </p>
                </div>

                <div className="mt-8 space-y-5">
                  {article.content.map((block, index) => {
                    if (block.type === "h2") {
                      return (
                        <h2
                          key={index}
                          id={`section-${index}`}
                          className="scroll-mt-28 pt-2 font-heading text-lg font-bold text-foreground-950 md:text-xl"
                        >
                          {block.text}
                        </h2>
                      );
                    }
                    if (block.type === "quote") {
                      return (
                        <blockquote
                          key={index}
                          className="rounded-card bg-secondary-50 px-5 py-4 font-heading text-sm italic text-secondary-900 md:text-base"
                        >
                          <i className="ri-double-quotes-l mr-1 text-primary-400" />
                          {block.text}
                        </blockquote>
                      );
                    }
                    return (
                      <p key={index} className="text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
                        {block.text}
                      </p>
                    );
                  })}
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                  <span className="text-sm text-foreground-500">相关标签：</span>
                  {article.tags.map((t) => (
                    <span
                      key={t}
                      className="rounded-full border border-background-300 px-3 py-1 text-xs font-label text-foreground-700"
                    >
                      {t}
                    </span>
                  ))}
                </div>

                <div className="mt-6 rounded-card border border-background-200 bg-background-100 p-5 md:p-6">
                  <p className="text-sm leading-relaxed text-foreground-600">
                    本文为公开讨论的二次归纳，不代表任何一方的立场。若内容存在事实偏差，欢迎通过
                    <Link to="/contact" className="mx-1 text-primary-700 underline cursor-pointer">
                      联系我们
                    </Link>
                    反馈，我们会尽快核对修正。
                  </p>
                </div>
              </div>

              <aside className="flex flex-col gap-5 lg:sticky lg:top-24 lg:self-start">
                {headings.length > 0 && (
                  <div className="rounded-card border border-background-200 bg-background-50 p-5">
                    <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
                      <i className="ri-list-check-2 text-primary-600" />
                      内容目录
                    </h2>
                    <ul className="mt-3 space-y-2">
                      {article.content.map((block, index) =>
                        block.type === "h2" ? (
                          <li key={index}>
                            <a
                              href={`#section-${index}`}
                              className="block text-sm text-foreground-600 transition-colors hover:text-primary-700 cursor-pointer"
                            >
                              {block.text}
                            </a>
                          </li>
                        ) : null,
                      )}
                    </ul>
                  </div>
                )}

                <div className="rounded-card border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-sm font-semibold text-foreground-950">同专题其他速览</h2>
                  <ul className="mt-3 space-y-3">
                    {related.map((r) => (
                      <li key={r.id} className="border-b border-background-200 pb-3 last:border-b-0 last:pb-0">
                        <Link
                          to={`/article/${r.id}`}
                          className="text-sm leading-snug text-foreground-700 transition-colors hover:text-primary-700 cursor-pointer"
                        >
                          {r.title}
                        </Link>
                        <p className="mt-1 text-xs text-foreground-500">{r.readTime} · 热度 {r.hot}</p>
                      </li>
                    ))}
                    {related.length === 0 && (
                      <li className="text-sm text-foreground-500">暂时没有同专题内容。</li>
                    )}
                  </ul>
                  <Link
                    to="/topics"
                    className="mt-4 flex items-center gap-1 text-xs font-label text-primary-700 cursor-pointer hover:underline"
                  >
                    进入完整专题库
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </article>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}